import type { PcSlotKey, SlotConfig } from '@/lib/pc-builder/types';

export const PC_SLOTS: SlotConfig[] = [
  {
    key: 'cpu',
    label: 'CPU',
    required: true,
    allowQuantity: false,
    icon: 'CPU',
    searchKeywords: ['cpu', 'processor', 'intel core', 'amd ryzen'],
    categoryHints: ['cpu', 'vi-xu-ly'],
    excludeKeywords: ['tản nhiệt', 'fan cpu'],
  },
  {
    key: 'mainboard',
    label: 'Mainboard',
    required: true,
    allowQuantity: false,
    icon: 'MB',
    searchKeywords: ['mainboard', 'motherboard', 'bo mach chu', 'chipset'],
    categoryHints: ['mainboard', 'bo-mach-chu', 'motherboard'],
  },
  {
    key: 'ram',
    label: 'RAM',
    required: true,
    allowQuantity: true,
    icon: 'RAM',
    searchKeywords: ['ram', 'ddr4', 'ddr5', 'memory'],
    categoryHints: ['ram', 'bo-nho'],
  },
  {
    key: 'ssd',
    label: 'SSD',
    required: true,
    allowQuantity: true,
    icon: 'SSD',
    searchKeywords: ['ssd', 'nvme', 'm.2'],
    categoryHints: ['ssd'],
  },
  {
    key: 'hdd',
    label: 'HDD',
    required: false,
    allowQuantity: true,
    icon: 'HDD',
    searchKeywords: ['hdd', 'hard disk'],
    categoryHints: ['hdd', 'o-cung'],
  },
  {
    key: 'vga',
    label: 'VGA',
    required: false,
    allowQuantity: false,
    icon: 'VGA',
    searchKeywords: ['vga', 'gpu', 'rtx', 'gtx', 'radeon'],
    categoryHints: ['vga', 'card-man-hinh', 'gpu'],
  },
  {
    key: 'psu',
    label: 'Nguồn PSU',
    required: true,
    allowQuantity: false,
    icon: 'PSU',
    searchKeywords: ['nguon', 'psu', 'power supply', '80 plus'],
    categoryHints: ['psu', 'nguon'],
  },
  {
    key: 'case',
    label: 'Case',
    required: true,
    allowQuantity: false,
    icon: 'CASE',
    searchKeywords: ['case', 'vo may', 'thung may'],
    categoryHints: ['case', 'vo-may', 'thung-may'],
  },
  {
    key: 'cooler',
    label: 'Tản nhiệt CPU',
    required: false,
    allowQuantity: false,
    icon: 'COOL',
    searchKeywords: ['tan nhiet', 'cooler', 'aio', 'heatsink', 'fan cpu'],
    categoryHints: ['tan-nhiet', 'cooler'],
  },
  {
    key: 'monitor',
    label: 'Màn hình',
    required: false,
    allowQuantity: true,
    icon: 'MON',
    searchKeywords: ['man hinh', 'monitor', 'gaming monitor'],
    categoryHints: ['man-hinh', 'monitor'],
  },
  {
    key: 'keyboard',
    label: 'Bàn phím',
    required: false,
    allowQuantity: true,
    icon: 'KB',
    searchKeywords: ['ban phim', 'keyboard', 'mechanical'],
    categoryHints: ['ban-phim', 'keyboard'],
  },
  {
    key: 'mouse',
    label: 'Chuột',
    required: false,
    allowQuantity: true,
    icon: 'MS',
    searchKeywords: ['chuot', 'mouse', 'dpi'],
    categoryHints: ['chuot', 'mouse'],
  },
  {
    key: 'headset',
    label: 'Tai nghe',
    required: false,
    allowQuantity: true,
    icon: 'HS',
    searchKeywords: ['tai nghe', 'headset', 'headphone'],
    categoryHints: ['tai-nghe', 'headset'],
  },
  {
    key: 'fan',
    label: 'Fan / Phụ kiện',
    required: false,
    allowQuantity: true,
    icon: 'FAN',
    searchKeywords: ['fan', 'phu kien', 'rgb', 'hub fan'],
    categoryHints: ['fan', 'phu-kien'],
  },
];

export const SLOT_MAP: Record<PcSlotKey, SlotConfig> = Object.fromEntries(
  PC_SLOTS.map((slot) => [slot.key, slot])
) as Record<PcSlotKey, SlotConfig>;

export interface BuilderPreset {
  id: string;
  label: string;
  description: string;
  budgetMin: number;
  budgetMax: number;
  focus: string;
  slotQueries: Partial<Record<PcSlotKey, string>>;
  requiredSlots: PcSlotKey[];
}

export const BUILDER_PRESETS: BuilderPreset[] = [
  {
    id: 'office-10',
    label: 'Văn phòng 10 triệu',
    description: 'Ưu tiên tiết kiệm điện, chạy tác vụ văn phòng ổn định.',
    budgetMin: 8000000,
    budgetMax: 12000000,
    focus: 'Office',
    slotQueries: {
      cpu: 'intel core i3',
      ram: 'ddr4 16gb',
      ssd: 'ssd nvme 500gb',
      monitor: '24 inch',
    },
    requiredSlots: ['cpu', 'mainboard', 'ram', 'ssd', 'psu', 'case'],
  },
  {
    id: 'gaming-20',
    label: 'Gaming 20 triệu',
    description: 'Chơi game 1080p/1440p, cân bằng CPU và GPU.',
    budgetMin: 18000000,
    budgetMax: 24000000,
    focus: 'Gaming',
    slotQueries: {
      cpu: 'ryzen 5',
      mainboard: 'b650',
      ram: 'ddr5 32gb',
      ssd: '1tb nvme',
      vga: 'rtx 4060',
      psu: '650w',
    },
    requiredSlots: ['cpu', 'mainboard', 'ram', 'ssd', 'vga', 'psu', 'case'],
  },
  {
    id: 'design-30',
    label: 'Đồ họa 30 triệu',
    description: 'Tối ưu RAM/VRAM cho dựng hình, edit video.',
    budgetMin: 26000000,
    budgetMax: 36000000,
    focus: 'Design',
    slotQueries: {
      cpu: 'intel core i7',
      ram: 'ddr5 64gb',
      ssd: '2tb nvme',
      vga: 'rtx 4070',
      psu: '750w',
    },
    requiredSlots: ['cpu', 'mainboard', 'ram', 'ssd', 'vga', 'psu', 'case'],
  },
  {
    id: 'stream-50',
    label: 'Stream 50 triệu+',
    description: 'Hiệu năng cao cho stream, gaming nặng và đa nhiệm.',
    budgetMin: 45000000,
    budgetMax: 100000000,
    focus: 'Streaming',
    slotQueries: {
      cpu: 'intel core i9',
      ram: 'ddr5 64gb',
      ssd: '2tb nvme',
      vga: 'rtx 4080',
      psu: '850w',
      cooler: 'aio 360',
    },
    requiredSlots: ['cpu', 'mainboard', 'ram', 'ssd', 'vga', 'psu', 'case', 'cooler'],
  },
];
